// File: src/pages/Login.js
import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  InputAdornment,
  Avatar,
  Container,
} from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined } from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext";
import { login } from "../api/axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login: contextLogin } = useContext(AuthContext);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (pw) =>
    /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@#]{8,}$/.test(pw);

  const handleLogin = useCallback(async () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required.";
    else if (!validateEmail(email)) newErrors.email = "Invalid email.";

    if (!password) newErrors.password = "Password is required.";
    else if (!validatePassword(password))
      newErrors.password = "Min 8 chars, at least one letter & one number.";

    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    try {
      const res = await login({ email, password });

      // Save token
      localStorage.setItem("token", res.data.token);

      // Use your login context method
      contextLogin({ email: res.data.user.email, name: res.data.user.name });

      toast.success("Logged in successfully!");

      navigate("/", { replace: true });
    } catch (err) {
      const message =
        err.response?.data?.error ||
        (err.request ? "Server not responding. Please try again later." : "Login failed. Please check credentials.");

      toast.error(message);

      setErrors({ general: message });
    }
  }, [email, password, contextLogin, navigate]);

  useEffect(() => {
    const onKey = (e) => e.key === "Enter" && handleLogin();
    window.addEventListener("keypress", onKey);
    return () => window.removeEventListener("keypress", onKey);
  }, [handleLogin]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #6E8EF8, #A5DCF8)",
        p: 2,
      }}
    >
      <Container maxWidth="xs">
        <Paper elevation={6} sx={{ p: 4, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Avatar sx={{ bgcolor: "primary.main", mb: 1 }}>
              <LockOutlined />
            </Avatar>
            <Typography variant="h5">Bulk WhatsApp Message</Typography>
          </Box>

          {errors.general && (
            <Typography variant="body2" color="error" mb={1}>
              {errors.general}
            </Typography>
          )}

          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
          />

          <TextField
            fullWidth
            label="Password"
            variant="outlined"
            margin="normal"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 2, mb: 1, py: 1.5 }}
            onClick={handleLogin}
          >
            Login
          </Button>

          <Box textAlign="center" mt={1}>
            <Link to="/forgot-password" style={{ textDecoration: "none" }}>
              <Typography variant="body2" color="primary">
                Forgot Password?
              </Typography>
            </Link>
            <Typography variant="body2" mt={1}>
              Don’t have an account?{" "}
              <Link to="/signup" style={{ textDecoration: "none" }}>
                <Typography component="span" color="primary">
                  Sign Up
                </Typography>
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
